import { useSearchParams } from 'react-router-dom'

export type DeferredRevenueUrlFilters = {
  customerIds: string[]
  productIds: string[]
  invoiceIds: string[]
  excludeFullyRecognized: boolean | undefined
}

/**
 * Reads the deferred revenue dashboard filters from the URL query params.
 * Matches the page-level convention where filters live on the URL (mirrors
 * the journal report export pattern) so header/export surfaces and the
 * table stay in sync without a shared store.
 */
export const useDeferredRevenueFiltersFromUrl =
  (): DeferredRevenueUrlFilters => {
    const [searchParams] = useSearchParams()

    // `?.trim()` leaves the value as `undefined` when the param is absent
    // (optional-chain short-circuit on a `null` return from `get`), so the
    // guard below checks for `undefined` rather than `null`.
    const excludeFullyRecognizedParam = searchParams
      .get('excludeFullyRecognized')
      ?.trim()
    // Treat an empty value (e.g. `?excludeFullyRecognized=`) the same as an
    // absent param so we fall back to the backend default (`true`) rather
    // than silently flipping to `false`.
    const hasExcludeFullyRecognizedValue =
      excludeFullyRecognizedParam !== undefined &&
      excludeFullyRecognizedParam !== ''

    return {
      customerIds: searchParams.getAll('customerId'),
      productIds: searchParams.getAll('productId'),
      invoiceIds: searchParams.getAll('invoiceId'),
      excludeFullyRecognized: hasExcludeFullyRecognizedValue
        ? excludeFullyRecognizedParam === 'true'
        : undefined
    }
  }
